// UI 状態: ビュー (ズーム・パン)、ツール、ダイアログ。履歴・保存の対象外。
import { defineStore } from 'pinia'
import { ref, computed, watch } from 'vue'
import type { Pt } from '@/types/geometry'

export type ToolId =
  | 'move' | 'hand' | 'zoom'
  | 'marquee' | 'lasso' | 'wand' | 'quickSelect' | 'crop' | 'eyedropper'
  | 'brush' | 'eraser' | 'fill' | 'gradient' | 'text' | 'shape' | 'pen'
  | 'clone' | 'heal' | 'spotHeal' | 'dodge' | 'burn' | 'sponge' | 'blurTool' | 'sharpenTool' | 'smudge'

/** Phase 0 で実際に動作するツール */
export const IMPLEMENTED_TOOLS: ReadonlySet<ToolId> = new Set<ToolId>(['move', 'hand', 'zoom', 'marquee', 'lasso', 'wand', 'quickSelect', 'brush', 'eraser', 'eyedropper', 'fill', 'gradient', 'crop', 'text', 'shape', 'pen', 'clone', 'heal', 'spotHeal', 'dodge', 'burn', 'sponge', 'blurTool', 'sharpenTool', 'smudge'])

export type SelectionMode = 'new' | 'add' | 'subtract' | 'intersect'

export interface ToolOptions {
  selectionMode: SelectionMode
  marqueeShape: 'rect' | 'ellipse'
  lassoKind: 'free' | 'polygon' | 'magnetic'
  feather: number
  antialias: boolean
  wandTolerance: number
  wandContiguous: boolean
  wandSampleAll: boolean
  quickSize: number
  quickTolerance: number
  quickSampleAll: boolean
  fillTolerance: number
  fillContiguous: boolean
  fillSampleAll: boolean
  fillOpacity: number
  gradientStyle: 'linear' | 'radial' | 'angle' | 'reflected' | 'diamond'
  gradientReverse: boolean
  gradientDither: boolean
  gradientOpacity: number
  gradientToTransparent: boolean
  /** カスタムのストップ (null なら描画色 → 背景色) */
  gradientStops: { pos: number; hex: string; alpha: number }[] | null
  shapeType: 'rect' | 'ellipse' | 'polygon' | 'line'
  shapeRadius: number
  shapeSides: number
  shapeStar: number
  shapeFill: boolean
  shapeStroke: boolean
  shapeStrokeWidth: number
}

export interface BrushSettings {
  size: number
  hardness: number
  opacity: number
  flow: number
  spacing: number
  smoothing: number
  pencil: boolean
  pressureSize: boolean
  pressureFlow: boolean
  /** 先端の回転 (度) */
  angle: number
  /** 真円率 0.05..1 */
  roundness: number
  /** 散布 (直径比) */
  scatter: number
  /** サイズのジッター 0..1 */
  sizeJitter: number
  /** 'round' またはカスタム先端 id */
  tipId: string
}

export const DEFAULT_BRUSH: BrushSettings = { size: 30, hardness: 0.8, opacity: 1, flow: 1, spacing: 0.1, smoothing: 0, pencil: false, pressureSize: true, pressureFlow: false, angle: 0, roundness: 1, scatter: 0, sizeJitter: 0, tipId: 'round' }

export interface ViewState {
  /** 表示倍率 (1 = 100%) */
  zoom: number
  /** 文書原点のビューポート内 CSS px 位置 */
  pan: Pt
}

export const ZOOM_MIN = 1 / 64
export const ZOOM_MAX = 64
export const ZOOM_STEPS = [1 / 64, 1 / 32, 1 / 16, 1 / 8, 1 / 4, 1 / 3, 1 / 2, 2 / 3, 1, 2, 3, 4, 6, 8, 12, 16, 24, 32, 64]

function loadFlag(key: string, def: boolean): boolean {
  try {
    const v = localStorage.getItem(key)
    return v === null ? def : v === '1'
  } catch {
    return def
  }
}

export const useUiStore = defineStore('ui', () => {
  const view = ref<ViewState>({ zoom: 1, pan: { x: 0, y: 0 } })
  const viewportSize = ref({ w: 0, h: 0 })
  const activeTool = ref<ToolId>('move')
  /** Space 押下中などの一時ツール */
  const temporaryTool = ref<ToolId | null>(null)
  const effectiveTool = computed(() => temporaryTool.value ?? activeTool.value)
  const dialog = ref<'new' | 'export' | 'about' | 'shortcuts' | 'selectionModify' | 'canvasSize' | 'imageSize' | 'layerStyle' | 'filter' | 'liquify' | 'colorRange' | 'refineEdge' | 'aiConsent' | 'fill' | 'stroke' | 'rotateArbitrary' | 'fontEmbed' | null>(null)
  const dialogArg = ref<string | null>(null)
  const toolOptions = ref<ToolOptions>({
    selectionMode: 'new',
    marqueeShape: 'rect',
    lassoKind: 'free',
    feather: 0,
    antialias: true,
    wandTolerance: 32,
    wandContiguous: true,
    wandSampleAll: false,
    quickSize: 30,
    quickTolerance: 40,
    quickSampleAll: true,
    fillTolerance: 32,
    fillContiguous: true,
    fillSampleAll: false,
    fillOpacity: 1,
    gradientStyle: 'linear',
    gradientReverse: false,
    gradientDither: false,
    gradientOpacity: 1,
    gradientToTransparent: false,
    gradientStops: null,
    shapeType: 'rect',
    shapeRadius: 0,
    shapeSides: 5,
    shapeStar: 0,
    shapeFill: true,
    shapeStroke: false,
    shapeStrokeWidth: 3
  })
  const cursorDocPos = ref<Pt | null>(null)
  const foreground = ref('#000000')
  const background = ref('#ffffff')
  const brush = ref<BrushSettings>({ ...DEFAULT_BRUSH })
  const eraser = ref<BrushSettings>({ ...DEFAULT_BRUSH, size: 40, hardness: 1, pressureSize: false })
  /** レタッチ系ブラシの設定 (サイズ等は brush と共有) */
  const retouch = ref({ size: 40, hardness: 0.5, exposure: 0.3, strength: 0.5, cloneAligned: true, spongeDesaturate: true, flow: 0.5 })
  /** コピースタンプのソース (文書座標) と、ストローク開始時に確定したオフセット */
  const cloneSource = ref<Pt | null>(null)
  const cloneOffset = ref<Pt | null>(null)

  // --- ワークスペース (ツールオプション / ブラシ / 色) の保存 ---
  const WORKSPACE_KEY = 'bpe.workspace.v1'
  function mergeInto<T extends object>(target: T, raw: unknown): void {
    if (!raw || typeof raw !== 'object') return
    for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
      const cur = (target as Record<string, unknown>)[k]
      if (cur === undefined || typeof cur !== typeof v || v === null) continue
      ;(target as Record<string, unknown>)[k] = v
    }
  }
  try {
    const raw = localStorage.getItem(WORKSPACE_KEY)
    if (raw) {
      const ws = JSON.parse(raw) as Record<string, unknown>
      mergeInto(toolOptions.value, ws.toolOptions)
      mergeInto(brush.value, ws.brush)
      mergeInto(eraser.value, ws.eraser)
      mergeInto(retouch.value, ws.retouch)
      if (typeof ws.foreground === 'string' && /^#[0-9a-f]{6}$/i.test(ws.foreground)) foreground.value = ws.foreground
      if (typeof ws.background === 'string' && /^#[0-9a-f]{6}$/i.test(ws.background)) background.value = ws.background
    }
  } catch {
    /* 壊れていれば既定値 */
  }
  let wsTimer: ReturnType<typeof setTimeout> | null = null
  watch(
    [toolOptions, brush, eraser, retouch, foreground, background],
    () => {
      if (wsTimer !== null) clearTimeout(wsTimer)
      wsTimer = setTimeout(() => {
        wsTimer = null
        try {
          localStorage.setItem(WORKSPACE_KEY, JSON.stringify({ toolOptions: toolOptions.value, brush: brush.value, eraser: eraser.value, retouch: retouch.value, foreground: foreground.value, background: background.value }))
        } catch {
          /* ignore */
        }
      }, 300)
    },
    { deep: true }
  )
  /** 保存したワークスペース (ツール設定・パネル幅・タブ・ショートカット以外) を消して再読み込み */
  function resetWorkspace(): void {
    try {
      for (const k of [WORKSPACE_KEY, 'bpe.layout.v1', 'bpe.dock.tab', 'bpe.rulers.v1', 'bpe.guides.v1', 'bpe.grid.v1', 'bpe.snap.v1']) localStorage.removeItem(k)
    } catch {
      /* ignore */
    }
    location.reload()
  }
  const colorPickerOpen = ref<'fg' | 'bg' | null>(null)
  /** ツールガイド (F1) を開いているか */
  const toolGuideOpen = ref(false)
  /** 表示: 定規 / ガイド / グリッド / スナップ */
  const showRulers = ref(loadFlag('bpe.rulers.v1', true))
  const showGuides = ref(true)
  const showGrid = ref(false)
  const gridSize = ref(50)
  const snapEnabled = ref(true)
  /** クイックマスクモード (Q) */
  const quickMask = ref(false)
  /** ドラッグ中の仮ガイド (定規からのドラッグ) */
  const draggingGuide = ref<{ axis: 'x' | 'y'; position: number } | null>(null)
  /** 自由変形の開始要求 (インクリメントで通知) */
  const transformRequest = ref(0)
  /** 変形 / 切り抜きの操作中 (ショートカットの一部を抑止) */
  const modalTool = ref<'transform' | 'crop' | 'text' | 'pen' | null>(null)
  /** 編集中のテキストレイヤー ID */
  const editingTextId = ref<string | null>(null)
  /** テキストツールの既定スタイル (最後に使ったもの) */
  const textStyle = ref<import('@/types/document').TextStyle>({ fontFamily: 'Noto Sans JP', fontSize: 48, fontWeight: 400, italic: false, hex: '#000000', lineHeight: 1.2, letterSpacing: 0, align: 'left', orientation: 'horizontal' })
  /** 変形中の表示用パラメータ (オプションバーが編集する) */
  const transformState = ref<{ scaleX: number; scaleY: number; angle: number; mode: 'free' | 'distort' | 'perspective' | 'warp'; canDistort: boolean; quad?: Pt[] | null; warp?: Pt[] | null } | null>(null)
  /** オプションバーからの確定 / 取消要求 */
  const modalToolCommand = ref<'commit' | 'cancel' | null>(null)

  function swapColors() {
    const f = foreground.value
    foreground.value = background.value
    background.value = f
  }
  function resetColors() {
    foreground.value = '#000000'
    background.value = '#ffffff'
  }
  /** 現在のブラシ系ツールの設定 */
  const RETOUCH_TOOLS: ToolId[] = ['clone', 'heal', 'spotHeal', 'dodge', 'burn', 'sponge', 'blurTool', 'sharpenTool', 'smudge']
  function isRetouchTool(t: ToolId = effectiveTool.value): boolean {
    return RETOUCH_TOOLS.includes(t)
  }
  function activeBrushSettings(): BrushSettings {
    if (isRetouchTool()) return { ...brush.value, size: retouch.value.size, hardness: retouch.value.hardness, flow: retouch.value.flow, opacity: 1 }
    return effectiveTool.value === 'eraser' ? eraser.value : brush.value
  }
  function adjustBrushSize(direction: 1 | -1) {
    const b = isRetouchTool() ? retouch.value : activeBrushSettings()
    const steps = [1, 2, 3, 4, 5, 7, 9, 12, 15, 20, 25, 30, 40, 50, 65, 80, 100, 125, 150, 200, 250, 300, 400, 500]
    const cur = b.size
    const next = direction > 0 ? steps.find(v => v > cur) ?? Math.min(2000, cur + 100) : [...steps].reverse().find(v => v < cur) ?? 1
    b.size = next
  }

  function setZoomAt(zoom: number, anchorCss: Pt): void {
    const z = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, zoom))
    const v = view.value
    // アンカー (CSS px) の下にある文書座標を固定したままズームする
    const docX = (anchorCss.x - v.pan.x) / v.zoom
    const docY = (anchorCss.y - v.pan.y) / v.zoom
    view.value = { zoom: z, pan: { x: anchorCss.x - docX * z, y: anchorCss.y - docY * z } }
  }

  function zoomStep(direction: 1 | -1, anchorCss?: Pt): void {
    const cur = view.value.zoom
    let next = cur
    if (direction > 0) next = ZOOM_STEPS.find(s => s > cur * 1.0001) ?? ZOOM_MAX
    else next = [...ZOOM_STEPS].reverse().find(s => s < cur * 0.9999) ?? ZOOM_MIN
    setZoomAt(next, anchorCss ?? { x: viewportSize.value.w / 2, y: viewportSize.value.h / 2 })
  }

  function panBy(dx: number, dy: number): void {
    const v = view.value
    view.value = { zoom: v.zoom, pan: { x: v.pan.x + dx, y: v.pan.y + dy } }
  }

  function fitToViewport(docW: number, docH: number, padding = 24): void {
    const { w, h } = viewportSize.value
    if (w <= 0 || h <= 0) return
    const z = Math.min((w - padding * 2) / docW, (h - padding * 2) / docH, ZOOM_MAX)
    const zoom = Math.max(ZOOM_MIN, z)
    view.value = { zoom, pan: { x: (w - docW * zoom) / 2, y: (h - docH * zoom) / 2 } }
  }

  function centerAt(zoom: number, docW: number, docH: number): void {
    const { w, h } = viewportSize.value
    view.value = { zoom, pan: { x: (w - docW * zoom) / 2, y: (h - docH * zoom) / 2 } }
  }

  function cssToDoc(p: Pt): Pt {
    const v = view.value
    return { x: (p.x - v.pan.x) / v.zoom, y: (p.y - v.pan.y) / v.zoom }
  }

  function docToCss(p: Pt): Pt {
    const v = view.value
    return { x: p.x * v.zoom + v.pan.x, y: p.y * v.zoom + v.pan.y }
  }

  return {
    view,
    viewportSize,
    activeTool,
    temporaryTool,
    effectiveTool,
    dialog,
    dialogArg,
    toolOptions,
    cursorDocPos,
    foreground,
    background,
    brush,
    eraser,
    retouch,
    cloneSource,
    cloneOffset,
    isRetouchTool,
    colorPickerOpen,
    showRulers,
    showGuides,
    showGrid,
    gridSize,
    snapEnabled,
    resetWorkspace,
    toolGuideOpen,
    quickMask,
    draggingGuide,
    transformRequest,
    modalTool,
    editingTextId,
    textStyle,
    transformState,
    modalToolCommand,
    swapColors,
    resetColors,
    activeBrushSettings,
    adjustBrushSize,
    setZoomAt,
    zoomStep,
    panBy,
    fitToViewport,
    centerAt,
    cssToDoc,
    docToCss
  }
})
